import React, { Fragment, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FiCheck, FiSearch, FiX } from "react-icons/fi";
import { PMS_LABELS, PMS_OPTIONS } from "../dashboard/derive";

const ONBOARDING_FILTERS = [
  { id: "all", label: "All onboarding" },
  { id: "onboarded", label: "Onboarded" },
  { id: "not_onboarded", label: "Not onboarded" },
];

const ACTIVE_FILTERS = [
  { id: "all", label: "All statuses" },
  { id: "verified", label: "Active (verified)" },
  { id: "unverified", label: "Unverified" },
  { id: "fully_active", label: "Verified & onboarded" },
];

function hostDisplayName(host) {
  const name = [host.firstName, host.lastName].filter(Boolean).join(" ").trim();
  return name || host.companyName || host.email;
}

function matchesSearch(host, query) {
  if (!query) return true;
  const haystack = [
    hostDisplayName(host),
    host.email,
    host.companyName,
    host.state,
    PMS_LABELS[host.pmsType] || host.pmsType,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function matchesOnboarding(host, filter) {
  if (filter === "all") return true;
  if (filter === "onboarded") return host.isOnBoarded;
  if (filter === "not_onboarded") return !host.isOnBoarded;
  return true;
}

function matchesActive(host, filter) {
  if (filter === "all") return true;
  if (filter === "verified") return host.isVerified;
  if (filter === "unverified") return !host.isVerified;
  if (filter === "fully_active") return host.isVerified && host.isOnBoarded;
  return true;
}

export default function HostBlastRecipientPicker({
  open,
  onClose,
  hosts,
  selectedIds,
  onChangeSelectedIds,
  loading = false,
  error = "",
}) {
  const [search, setSearch] = useState("");
  const [pmsFilter, setPmsFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [onboardingFilter, setOnboardingFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  const stateOptions = useMemo(() => {
    const states = new Set(
      hosts.map((host) => (host.state || "").trim()).filter(Boolean)
    );
    return Array.from(states).sort((a, b) => a.localeCompare(b));
  }, [hosts]);

  const filteredHosts = useMemo(() => {
    return hosts.filter((host) => {
      if (pmsFilter !== "all" && host.pmsType !== pmsFilter) return false;
      if (stateFilter !== "all" && host.state !== stateFilter) return false;
      if (!matchesOnboarding(host, onboardingFilter)) return false;
      if (!matchesActive(host, activeFilter)) return false;
      if (!matchesSearch(host, search.trim())) return false;
      return true;
    });
  }, [hosts, pmsFilter, stateFilter, onboardingFilter, activeFilter, search]);

  const filteredIds = useMemo(() => filteredHosts.map((host) => host.id), [filteredHosts]);
  const selectedCount = selectedIds.size;
  const filteredSelectedCount = filteredIds.filter((id) => selectedIds.has(id)).length;

  const toggleHost = (hostId) => {
    const next = new Set(selectedIds);
    if (next.has(hostId)) next.delete(hostId);
    else next.add(hostId);
    onChangeSelectedIds(next);
  };

  const selectFiltered = () => {
    const next = new Set(selectedIds);
    filteredIds.forEach((id) => next.add(id));
    onChangeSelectedIds(next);
  };

  const deselectFiltered = () => {
    const next = new Set(selectedIds);
    filteredIds.forEach((id) => next.delete(id));
    onChangeSelectedIds(next);
  };

  const selectAll = () => {
    onChangeSelectedIds(new Set(hosts.map((host) => host.id)));
  };

  const clearAll = () => {
    onChangeSelectedIds(new Set());
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-3 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <Dialog.Panel className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-zinc-200">
                <div className="border-b border-zinc-200 px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-zinc-900">
                        Select host recipients
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-zinc-500">
                        Filter hosts by PMS, state, and onboarding status, then select who should receive this blast.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
                      aria-label="Close recipient picker"
                    >
                      <FiX className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="border-b border-zinc-200 bg-zinc-50 px-5 py-4">
                  <div className="grid gap-3 lg:grid-cols-[1.2fr_repeat(4,minmax(0,1fr))]">
                    <div className="relative">
                      <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                      <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, email, company..."
                        className="w-full rounded-xl border border-zinc-300 bg-white py-2.5 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      />
                    </div>
                    <select
                      value={pmsFilter}
                      onChange={(e) => setPmsFilter(e.target.value)}
                      className="rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none"
                    >
                      {PMS_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      className="rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="all">All states</option>
                      {stateOptions.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <select
                      value={onboardingFilter}
                      onChange={(e) => setOnboardingFilter(e.target.value)}
                      className="rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none"
                    >
                      {ONBOARDING_FILTERS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      className="rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none"
                    >
                      {ACTIVE_FILTERS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={selectFiltered}
                      disabled={!filteredIds.length}
                      className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Select filtered ({filteredIds.length})
                    </button>
                    <button
                      type="button"
                      onClick={deselectFiltered}
                      disabled={!filteredSelectedCount}
                      className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Deselect filtered
                    </button>
                    <button
                      type="button"
                      onClick={selectAll}
                      disabled={!hosts.length}
                      className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Select all hosts
                    </button>
                    <button
                      type="button"
                      onClick={clearAll}
                      disabled={!selectedCount}
                      className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Clear selection
                    </button>
                    <span className="ml-auto text-xs text-zinc-500">
                      Showing {filteredHosts.length} of {hosts.length} · {selectedCount} selected
                    </span>
                  </div>
                </div>

                {error ? (
                  <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                ) : null}

                <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
                  {loading ? (
                    <div className="flex h-48 items-center justify-center text-sm text-zinc-500">
                      Loading hosts...
                    </div>
                  ) : filteredHosts.length === 0 ? (
                    <div className="flex h-48 items-center justify-center text-sm text-zinc-500">
                      No hosts match the current filters.
                    </div>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-white text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        <tr className="border-b border-zinc-200">
                          <th className="w-10 px-3 py-2"> </th>
                          <th className="px-3 py-2">Host</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">PMS</th>
                          <th className="px-3 py-2">State</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {filteredHosts.map((host) => {
                          const checked = selectedIds.has(host.id);
                          return (
                            <tr
                              key={host.id}
                              className={checked ? "bg-indigo-50/60" : "hover:bg-zinc-50"}
                            >
                              <td className="px-3 py-2.5">
                                <label className="inline-flex cursor-pointer items-center">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleHost(host.id)}
                                    className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                </label>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="font-medium text-zinc-900">{hostDisplayName(host)}</div>
                                {host.companyName ? (
                                  <div className="text-xs text-zinc-500">{host.companyName}</div>
                                ) : null}
                              </td>
                              <td className="px-3 py-2.5 text-zinc-700">{host.email}</td>
                              <td className="px-3 py-2.5 text-zinc-600">
                                {PMS_LABELS[host.pmsType] || host.pmsType || "—"}
                              </td>
                              <td className="px-3 py-2.5 text-zinc-600">{host.state || "—"}</td>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-wrap gap-1">
                                  <span
                                    className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                      host.isVerified
                                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                        : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                                    }`}
                                  >
                                    {host.isVerified ? "Verified" : "Unverified"}
                                  </span>
                                  <span
                                    className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                      host.isOnBoarded
                                        ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
                                        : "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200"
                                    }`}
                                  >
                                    {host.isOnBoarded ? "Onboarded" : "Needs onboarding"}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-5 py-3">
                  <p className="text-sm text-zinc-600">
                    <span className="font-semibold text-zinc-900">{selectedCount}</span> host
                    {selectedCount === 1 ? "" : "s"} selected for this blast
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                  >
                    <FiCheck className="h-4 w-4" />
                    Done
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
